import React from 'react';
import {Link} from 'gatsby';
import moment from 'moment';
import 'moment/locale/nl';
import {NumberParam, useQueryParam} from 'use-query-params';

moment.locale('nl');

export default function News({additionalData}): React.JSX.Element {
  const numbersPerPage = 10;
  const paginatedData = [];
  for (let i = 0; i < additionalData.allNews.length; i += numbersPerPage) {
    const chunk = additionalData.allNews.slice(i, i + numbersPerPage);
    paginatedData.push(chunk);
  }

  let [page, setPage] = useQueryParam('page', NumberParam);
  page = page ?? 0;

  if (typeof (paginatedData[page]) == 'undefined') {
    setPage(0, 'push');
    return <></>;
  }

  const pagerMaxDiff = 2;

  return (
    <section className="vl-region">
      <div className="vl-layout">
        <div className="vl-grid vl-grid--is-stacked">
          {paginatedData[page].map(
            (newsNode: Queries.node__news, idx: number) => (
              <div
                key={idx}
                className="vl-col--12-12 vl-col--12-12--m vl-col--12-12--s"
              >
                <Link
                  to={newsNode.path.alias}
                  key={idx}
                  className="vl-spotlight vl-spotlight--no-border"
                >
                  <article role="none">
                    <h3 className="vl-spotlight__title line-clamp-2">
                      {newsNode.title}
                    </h3>
                    <div className="vl-spotlight__text">
                      <div className="vl-icon-wrapper">
                        <i className="vl-icon vl-icon--before vl-icon--light vl-vi vl-vi-calendar"></i>
                        <time
                          dateTime={newsNode.field_date}
                          className="vl-annotation"
                        >
                          {moment(newsNode.field_date).format('D MMMM YYYY')}
                        </time>
                      </div>
                    </div>
                    <div
                      className="vl-spotlight__text line-clamp-4"
                      dangerouslySetInnerHTML={{
                        __html: newsNode.body?.processed,
                      }}
                    />
                  </article>
                </Link>
              </div>
            ),
          )}
          <div className="vl-col--12-12 vl-col--12-12--m vl-col--12-12--s">
            {paginatedData.length > 1 ? (
              <>
                <hr className="vl-u-hr "></hr>
                {/* Desktop pager */}
                <div className="vl-pager vl-u-spacer-top--medium vl-u-hidden--s">
                  <ul className="vl-pager__list">

                    {paginatedData[+page - 1]
                      ? (
                        <li className="vl-pager__element">
                          <Link
                            to={`?page=${+page - 1}`}
                            className="vl-pager__element__cta vl-link vl-link--bold"
                          >
                            <i
                              className="vl-link__icon vl-link__icon--before vl-vi vl-vi-arrow-left-fat"
                              aria-hidden="true"
                            ></i>
                            Vorige
                            <span className="vl-u-visually-hidden"> {numbersPerPage} rijen</span>
                          </Link>
                        </li>)
                      : <></>
                    }

                    {page >= pagerMaxDiff ? (
                      <>
                        <li className="vl-pager__element">
                          <Link
                            to="?page=0"
                            className="vl-pager__element__cta vl-link vl-link--bold"
                          >
                            1
                          </Link>
                        </li>
                        <li className="vl-pager__element">
                          <div className="vl-pager__element__cta">...</div>
                        </li>
                      </>
                    ) : <></>}

                    {paginatedData.map((_, idx) => {
                      // calculate the difference between the current page and the page we are looping over
                      const diff = Math.abs(+page - idx);
                      if (diff < pagerMaxDiff) {
                        return (
                          <li className="vl-pager__element" key={idx}>
                            <Link
                              to={`?page=${idx}`}
                              className={`vl-pager__element__cta vl-link vl-link--bold ${(idx === +page) ? 'vl-link--active' : ''}`}
                            >
                              {idx + 1}
                            </Link>
                          </li>
                        );
                      }
                    })}

                    {page <= paginatedData.length - pagerMaxDiff - 1 ? (
                      <>
                        <li className="vl-pager__element">
                          <div className="vl-pager__element__cta">...</div>
                        </li>
                        <li className="vl-pager__element">
                          <Link
                            to={`?page=${paginatedData.length - 1}`}
                            className="vl-pager__element__cta vl-link vl-link--bold"
                          >
                            {paginatedData.length}
                          </Link>
                        </li>
                      </>
                    ) : <></>}

                    {paginatedData[+page + 1]
                      ? (
                        <li className="vl-pager__element">
                          <Link
                            to={`?page=${+page + 1}`}
                            className="vl-pager__element__cta vl-link vl-link--bold"
                          >
                            Volgende
                            <span className="vl-u-visually-hidden"> {numbersPerPage} rijen</span>
                            <i
                              className="vl-link__icon vl-link__icon--after vl-vi vl-vi-arrow-right-fat"
                              aria-hidden="true"
                            ></i>
                          </Link>
                        </li>)
                      : <></>
                    }
                  </ul>
                </div>
              </>
            ) : <></>}
            {/* Mobile pager */}
            <div className="vl-pager vl-pager--align-center vl-u-spacer-top--medium vl-u-hidden vl-u-visible--s">
              <ul className="vl-pager__list">
                {paginatedData[+page - 1]
                  ? (
                    <li className="vl-pager__element">
                      <Link
                        to={`?page=${+page - 1}`}
                        className="vl-pager__element__cta vl-link vl-link--bold"
                      >
                        <i
                          className="vl-link__icon vl-link__icon--before vl-vi vl-vi-arrow-left-fat"
                          aria-hidden="true"
                        ></i>
                        Vorige
                        <span className="vl-u-visually-hidden"> {numbersPerPage} rijen</span>
                      </Link>
                    </li>)
                  : <></>
                }
                {paginatedData[+page + 1]
                  ? (
                    <li className="vl-pager__element">
                      <Link
                        to={`?page=${+page + 1}`}
                        className="vl-pager__element__cta vl-link vl-link--bold"
                      >
                        Volgende
                        <span className="vl-u-visually-hidden"> {numbersPerPage} rijen</span>
                        <i
                          className="vl-link__icon vl-link__icon--after vl-vi vl-vi-arrow-right-fat"
                          aria-hidden="true"
                        ></i>
                      </Link>
                    </li>)
                  : <></>
                }
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
